import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { apiGet, apiPost } from '../lib/api';
import { CertCard } from '../components/CertCard';
import type { CertData } from '../components/CertCard';
// Inline cert under its standard. No separate "your certifications" section.
import { LoadingDots } from '../components/LoadingDots';
import { Feedback } from '../components/Feedback';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { STANDARDS_DISPLAY } from '../lib/standards';

interface StandardInfo {
  code: string;
  name: string;
  description: string;
  available: boolean;
}

const STANDARDS: StandardInfo[] = [
  { code: 'EMRG', name: 'Emergency Communications', description: 'Public safety alerts, first response coordination, mass casualty, evacuation, shelter orders', available: true },
  { code: 'POLI', name: 'Law Enforcement', description: 'Patrol status, incident reports, officer safety, suspect tracking, perimeter control', available: false },
  { code: 'ENGY', name: 'Energy / Grid', description: 'Voltage monitoring, current sensing, outage detection, solar panel output, battery bank status', available: false },
  { code: 'H2OQ', name: 'Water Quality', description: 'pH, turbidity, dissolved oxygen, chlorine levels, flow rate, contamination alerts', available: false },
  { code: 'TRAX', name: 'Asset Tracking', description: 'GPS position, geofence alerts, door status, tamper detection, fleet management', available: false },
  { code: 'AGRO', name: 'Agriculture / Soil', description: 'Soil moisture, nutrient levels, temperature, rainfall, irrigation, frost alerts', available: false },
  { code: 'STRX', name: 'Structural Monitoring', description: 'Tilt angle, vibration, crack width, strain gauge, bridge and building health assessment', available: false },
  { code: 'MEDI', name: 'Medical Telemetry', description: 'Heart rate, SpO2, blood pressure, body temperature, patient ID, triage classification', available: false },
  { code: 'COLD', name: 'Cold Chain Logistics', description: 'Cargo temperature, humidity, door events, compliance tracking, excursion alerts', available: false },
  { code: 'ATMO', name: 'Air Quality', description: 'PM2.5, PM10, CO2, NO2, ozone, VOC levels, AQI calculation, pollution source tracking', available: false },
  { code: 'WTHR', name: 'Weather Reporting', description: 'Temperature, humidity, wind speed/direction, barometric pressure, rainfall, UV index', available: false },
];

const TIERS = [
  { value: 0, label: 'Tier 0 · NANO   51 bytes' },
  { value: 1, label: 'Tier 1 · MICRO  115 bytes' },
  { value: 2, label: 'Tier 2 · FULL   222 bytes' },
];

function isActive(c: CertData): boolean {
  const now = Math.floor(Date.now() / 1000);
  return c.status === 'active' && (!c.expiresAt || c.expiresAt > now);
}

/** Route: `/dashboard/certs`. Lists available standards and lets the user request / view active certs. */
export function CertsPage() {
  const { isAdmin } = useAuth();
  const [certs, setCerts] = useState<CertData[] | null>(null);
  const [activeStandard, setActiveStandard] = useState<string | null>(null);
  const [tier, setTier] = useState(0);
  const [type, setType] = useState<'test' | 'production'>('test');
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [availableCodes, setAvailableCodes] = useState<Set<string>>(new Set(['EMRG']));

  // Load certs. On error, set empty list ONLY if there's nothing yet —
  // never clobber a previously-loaded list with empty on a transient failure.
  const refresh = useCallback(async (): Promise<CertData[]> => {
    try {
      const data = await apiGet<CertData[]>('/v1/certs');
      setCerts(data);
      return data;
    } catch (err) {
      setCerts((prev) => prev ?? []);
      throw err;
    }
  }, []);

  useEffect(() => { refresh().catch((err: Error) => setFeedback({ type: 'error', msg: err.message || 'Unable to load certifications.' })); apiGet<{ schemas: Array<{ code: string; implemented?: boolean }> }>('/v1/schemas').then((r) => setAvailableCodes(new Set(r.schemas.filter((s) => s.implemented !== false).map((s) => s.code)))).catch((err: Error) => setFeedback({ type: 'error', msg: err.message || 'Unable to load standard availability.' })); }, [refresh]);

  function activeCertFor(schema: string): CertData | undefined {
    return certs?.find((c) => c.schema === schema && isActive(c));
  }

  async function handleRequest(e: FormEvent) {
    e.preventDefault();
    setFeedback(null);
    setSubmitting(true);
    try {
      await apiPost(`/v1/certs/request`, { schema: activeStandard, tier, type });
      // Wait for the refresh so the new cert is in state before we show success.
      const updated = await refresh();
      const justIssued = updated
        .filter((c) => c.schema === activeStandard && isActive(c))
        .sort((a, b) => b.issuedAt - a.issuedAt)[0];
      const days = type === 'test' ? 30 : 60;
      setFeedback({
        type: 'success',
        msg: `✓ ${activeStandard} ${type} certification issued — Tier ${justIssued?.tier ?? tier} · expires in ${days} days`,
      });
      setActiveStandard(null);
    } catch (e) {
      setFeedback({ type: 'error', msg: e instanceof Error ? e.message : 'request failed' });
    } finally {
      setSubmitting(false);
    }
  }

  // Renew handler — passed to CertCard so CertsPage stays the source of truth.
  const handleAfterCertChange = useCallback(async () => {
    try {
      await refresh();
      setFeedback({ type: 'success', msg: '✓ certification renewed' });
    } catch (e) {
      setFeedback({ type: 'error', msg: e instanceof Error ? e.message : 'refresh failed' });
    }
  }, [refresh]);

  return (
    <div>
      <div className="text-muted" style={{ marginBottom: 8 }}>── select your standard ──────────────────────────────────</div>

      {feedback && <Feedback type={feedback.type} message={feedback.msg} />}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12, marginBottom: 24 }}>
        {STANDARDS.map((s) => {
          const existing = activeCertFor(s.code);
          const available = availableCodes.has(s.code);
          return (
            <div key={s.code}>
            <div
              className="box"
              style={{
                opacity: 1,
                borderColor: activeStandard === s.code ? 'var(--color-accent)' : 'var(--color-border)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <div>
                  <strong>{(STANDARDS_DISPLAY as Record<string, string>)[s.code] ?? s.code}</strong> · {s.name}
                </div>
                <div className="text-muted">
                  {!available
                    ? 'reference codec unavailable'
                    : existing
                    ? <span className="text-success">✓ certified</span>
                    : <span className="text-success">✓ reference codec available</span>}
                </div>
              </div>
              <p className="text-muted" style={{ fontSize: 'var(--font-size-sm)', marginTop: 4 }}>{s.description}</p>
              <Link to={`/docs/standards/${s.code.toLowerCase()}`}>Read specification →</Link>
              {!isAdmin && available && !existing && <p className="text-muted">An organization owner or admin can request certification.</p>}

              {/* Available, not certified, not currently expanded */}
              {isAdmin && available && !existing && activeStandard !== s.code && (
                <div style={{ marginTop: 8 }}>
                  <button type="button" className="btn btn-primary" onClick={() => { setActiveStandard(s.code); setFeedback(null); }}>
                    get certified
                  </button>
                </div>
              )}

              {/* Request form */}
              {isAdmin && available && !existing && activeStandard === s.code && (
                <form onSubmit={handleRequest} style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--color-border)' }}>
                  <div className="text-muted" style={{ marginBottom: 8 }}>── request {s.code} certification ────────────────────</div>
                  <div className="form-row">
                    <label className="label">tier</label>
                    {TIERS.map((t) => (
                      <div key={t.value}>
                        <label>
                          <input type="radio" name="tier" value={t.value} checked={tier === t.value} onChange={() => setTier(t.value)} />
                          {' '}{t.label}
                        </label>
                      </div>
                    ))}
                  </div>
                  <div className="form-row">
                    <label className="label">type</label>
                    <div>
                      <label>
                        <input type="radio" name="type" value="test" checked={type === 'test'} onChange={() => setType('test')} />
                        {' '}test &nbsp;&nbsp; <span className="text-muted">30 days, one renewal, for development</span>
                      </label>
                    </div>
                    <div>
                      <label>
                        <input type="radio" name="type" value="production" checked={type === 'production'} onChange={() => setType('production')} />
                        {' '}production &nbsp;&nbsp; <span className="text-muted">60 days, renewable, free during beta</span>
                      </label>
                    </div>
                  </div>
                  <button type="submit" className="btn btn-primary" disabled={submitting} style={{ marginRight: 8 }}>
                    {submitting ? 'requesting...' : 'request certification'}
                  </button>
                  <button type="button" className="btn" onClick={() => setActiveStandard(null)}>cancel</button>
                </form>
              )}
            </div>

            {existing && (
              <div style={{ marginTop: 8, marginBottom: 4 }}>
                <CertCard cert={existing} onChange={handleAfterCertChange} />
              </div>
            )}
          </div>
          );
        })}
      </div>

      {certs === null && <LoadingDots />}
    </div>
  );
}
